import { buildCycleWave, type EngineParams, type EngineProfile, type TyreParams } from './engineModel';
import type { NoiseKind } from './mixer';

/**
 * One engine + tyre voice as a Web Audio graph. Works on any BaseAudioContext, so the offline lab
 * (OfflineAudioContext) renders exactly what the game plays.
 *
 *   cycle (ConstantSource, Hz) -> on-wave osc  -> gOn  -+
 *                              -> off-wave osc -> gOff -+-> lowpass (Q .5) -> body peak -> hp 28 Hz -> engineGain -+
 *                              -> x cyl/2 -> sine sub -> gSub -+                                                 |
 *   pink noise -> bandpass -> AM by the on-wave (intake chuff) -> intakeGain ------------------------------------+
 *   pops (overrun) -------------------------------------------------------------------------------------------+-> post
 *   white noise -> 2 narrow bands (squeal + octave), wobbling -> squealGain ---------------------------------+     |
 *   brown noise -> lowpass (tyre scrub) -> scrubGain ----------------------------------------------------------+     |
 *   white noise -> 2 bands, rough AM -> scrapeGain ------------------------------------------------------------+     v
 *                                                                                      post -> air lowpass -> pan -> out
 *
 * Everything is band-limited (PeriodicWave / sines / filtered noise); there is no waveshaper.
 */

export interface VoiceHost {
  ctx: BaseAudioContext;
  noise: Record<NoiseKind, AudioBuffer>;
}

interface Waves {
  on: PeriodicWave;
  off: PeriodicWave;
}

/** Makeup gain on the band-passed intake noise (see the constructor). */
export const INTAKE_MAKEUP = 9;
/** Peak gain of an overrun pop (relative to the voice). Kept ~10 dB over the engine: a crackle, not a gunshot. */
export const POP_GAIN = 0.55;

const waveCache =new WeakMap<BaseAudioContext, Map<string, Waves>>();

/** PeriodicWaves per (context, profile), built once. */
export function engineWaves(ctx: BaseAudioContext, p: EngineProfile): Waves {
  let m = waveCache.get(ctx);
  if (!m) {
    m = new Map();
    waveCache.set(ctx, m);
  }
  const key = `${p.id}:${p.cylinders}`;
  let w = m.get(key);
  if (!w) {
    const on = buildCycleWave(p, 'on');
    const off = buildCycleWave(p, 'off');
    w = { on: ctx.createPeriodicWave(on.real, on.imag, { disableNormalization: true }), off: ctx.createPeriodicWave(off.real, off.imag, { disableNormalization: true }) };
    m.set(key, w);
  }
  return w;
}

export interface Spatial {
  /** Distance gain (1 = player's own car). */
  gain: number;
  pan: number;
  /** Air-absorption lowpass (Hz). */
  cutoff: number;
  /** Doppler pitch ratio. */
  pitch: number;
}

export class EngineVoiceGraph {
  readonly out: GainNode;
  private readonly ctx: BaseAudioContext;
  private readonly host: VoiceHost;
  private readonly all: AudioNode[] = [];
  private readonly sources: AudioScheduledSourceNode[] = [];
  private readonly cycle: ConstantSourceNode;
  private readonly subMul: GainNode;
  private readonly oscOn: OscillatorNode;
  private readonly oscOff: OscillatorNode;
  private readonly gOn: GainNode;
  private readonly gOff: GainNode;
  private readonly gSub: GainNode;
  private readonly lp: BiquadFilterNode;
  private readonly body: BiquadFilterNode;
  private readonly engineGain: GainNode;
  private readonly intakeBp: BiquadFilterNode;
  private readonly intakeGain: GainNode;
  private readonly lopeLfo: OscillatorNode;
  private readonly lopeGain: GainNode;
  private readonly squealBp: BiquadFilterNode;
  private readonly squealBp2: BiquadFilterNode;
  private readonly squealGain: GainNode;
  private readonly scrubGain: GainNode;
  private readonly scrapeBp: BiquadFilterNode;
  private readonly scrapeGain: GainNode;
  private readonly post: GainNode;
  private readonly air: BiquadFilterNode;
  private readonly panner: StereoPannerNode;
  private profileKey = '';
  private disposed = false;

  constructor(host: VoiceHost, profile: EngineProfile) {
    this.host = host;
    const ctx = (this.ctx = host.ctx);
    const t = ctx.currentTime;
    const gain = (v: number): GainNode => {
      const g = ctx.createGain();
      g.gain.value = v;
      this.all.push(g);
      return g;
    };
    const filt = (type: BiquadFilterType, f: number, q: number): BiquadFilterNode => {
      const b = ctx.createBiquadFilter();
      b.type = type;
      b.frequency.value = f;
      b.Q.value = q;
      this.all.push(b);
      return b;
    };
    const osc = (type: OscillatorType, f: number): OscillatorNode => {
      const o = ctx.createOscillator();
      o.type = type;
      o.frequency.value = f;
      this.all.push(o);
      this.sources.push(o);
      return o;
    };
    const noise = (kind: NoiseKind): AudioBufferSourceNode => {
      const s = ctx.createBufferSource();
      s.buffer = host.noise[kind];
      s.loop = true;
      this.all.push(s);
      this.sources.push(s);
      return s;
    };

    this.out = gain(0);
    this.panner = ctx.createStereoPanner();
    this.all.push(this.panner);
    this.air = filt('lowpass', 16000, 0.5);
    this.post = gain(1);
    this.post.connect(this.air).connect(this.panner).connect(this.out);

    // Engine body.
    this.cycle = ctx.createConstantSource();
    this.cycle.offset.value = 0;
    this.all.push(this.cycle);
    this.sources.push(this.cycle);
    this.oscOn = osc('sine', 0);
    this.oscOff = osc('sine', 0);
    this.cycle.connect(this.oscOn.frequency);
    this.cycle.connect(this.oscOff.frequency);
    this.subMul = gain(profile.cylinders);
    const sub = osc('sine', 0);
    this.cycle.connect(this.subMul).connect(sub.frequency);
    this.gOn = gain(0);
    this.gOff = gain(0);
    this.gSub = gain(0);
    const sum = gain(1);
    this.oscOn.connect(this.gOn).connect(sum);
    this.oscOff.connect(this.gOff).connect(sum);
    sub.connect(this.gSub).connect(sum);
    this.lp = filt('lowpass', 800, 0.5);
    this.body = filt('peaking', profile.bodyHz, 1.1);
    this.body.gain.value = profile.bodyDb;
    const hp = filt('highpass', 28, 0.6);
    this.engineGain = gain(0);
    sum.connect(this.lp).connect(this.body).connect(hp).connect(this.engineGain).connect(this.post);

    // Intake: band of pink noise, amplitude-modulated by the firing pulses (a chuff, not a hiss).
    this.intakeBp = filt('bandpass', profile.intakeHz, 0.9);
    const am = gain(0.55);
    const amDepth = gain(0.45);
    this.oscOn.connect(amDepth).connect(am.gain);
    this.intakeGain = gain(0);
    // A one-octave band of pink noise is ~27 dB under the full-band noise: make that up so the chuff
    // is actually heard (it carries the idle on small speakers, where the sub-200 Hz body is lost),
    // and keep it out of the harsh 2-5 kHz band with its own lowpass.
    const intakeMakeup = gain(INTAKE_MAKEUP);
    noise('pink').connect(this.intakeBp).connect(am).connect(this.intakeGain).connect(intakeMakeup).connect(filt('lowpass', 1700, 0.5)).connect(this.post);

    // Idle lope.
    this.lopeLfo = osc('sine', 6);
    this.lopeGain = gain(0);
    this.lopeLfo.connect(this.lopeGain).connect(this.engineGain.gain);

    // Tyre squeal: two narrow noise bands an octave-and-a-bit apart, wobbling (a howl, not hiss).
    this.squealBp = filt('bandpass', 800, 14);
    this.squealBp2 = filt('bandpass', 1700, 11);
    const sq2 = gain(0.35);
    this.squealGain = gain(0);
    const sqSrc = noise('white');
    sqSrc.connect(this.squealBp).connect(this.squealGain);
    sqSrc.connect(this.squealBp2).connect(sq2).connect(this.squealGain);
    const sqLp = filt('lowpass', 3200, 0.5);
    this.squealGain.connect(sqLp).connect(this.post);
    const wob = osc('sine', 8.7);
    const wobDepth = gain(45);
    wob.connect(wobDepth).connect(this.squealBp.frequency);
    const wob2 = osc('sine', 5.3);
    const wob2Depth = gain(70);
    wob2.connect(wob2Depth).connect(this.squealBp2.frequency);

    // Scrub: low rumble of rubber dragging.
    this.scrubGain = gain(0);
    noise('brown').connect(filt('lowpass', 420, 0.7)).connect(this.scrubGain).connect(this.post);

    // Metal scrape: noise through a mid band + a ringing high band, rough AM.
    this.scrapeBp = filt('bandpass', 700, 3);
    const scrapeHi = filt('bandpass', 2300, 7);
    const scrapeAm = gain(0.6);
    this.scrapeGain = gain(0);
    const scSrc = noise('white');
    scSrc.connect(this.scrapeBp).connect(scrapeAm);
    scSrc.connect(scrapeHi).connect(gain(0.5)).connect(scrapeAm);
    scrapeAm.connect(this.scrapeGain).connect(filt('lowpass', 4500, 0.5)).connect(this.post);
    const amLfo = osc('triangle', 17);
    const amLfoDepth = gain(0.4);
    amLfo.connect(amLfoDepth).connect(scrapeAm.gain);

    this.setProfile(profile);
    const dur = (b: AudioBuffer | undefined): number => (b ? b.duration : 1);
    for (const s of this.sources) {
      if (s instanceof AudioBufferSourceNode) s.start(t, Math.random() * dur(s.buffer ?? undefined));
      else s.start(t);
    }
  }

  /** Swap the engine character (waves, body, sub ratio) for a newly assigned vehicle. */
  setProfile(p: EngineProfile): void {
    const key = `${p.id}:${p.cylinders}`;
    if (key === this.profileKey) return;
    this.profileKey = key;
    const w = engineWaves(this.ctx, p);
    this.oscOn.setPeriodicWave(w.on);
    this.oscOff.setPeriodicWave(w.off);
    const now = this.ctx.currentTime;
    this.subMul.gain.setValueAtTime(p.cylinders, now);
    this.body.frequency.setValueAtTime(p.bodyHz, now);
    this.body.gain.setValueAtTime(p.bodyDb, now);
    this.intakeBp.frequency.setValueAtTime(p.intakeHz, now);
    // apply() glides these two from here on: forget what it last asked for.
    this.targets[5] = NaN;
    this.targets[7] = NaN;
  }

  /**
   * Schedule one frame of parameters at audio time `now`. `snap` jumps instead of gliding (a fresh,
   * still-silent assignment). `lite` thins out distant voices.
   */
  apply(p: EngineParams, ty: TyreParams, sp: Spatial, now: number, snap = false, lite = 1): void {
    if (this.disposed) return;
    const cyc = p.cycleHz * sp.pitch;
    if (snap) {
      this.cycle.offset.cancelScheduledValues(now);
      this.cycle.offset.setValueAtTime(cyc, now);
      this.out.gain.cancelScheduledValues(now);
      this.out.gain.setValueAtTime(0, now);
      this.targets.fill(NaN);
    }
    // Pitch follows within ~25 ms (the model already slew-limits it).
    this.glide(0, this.cycle.offset, cyc, now, 0.025);
    this.glide(1, this.gOn.gain, p.onGain, now, 0.03);
    this.glide(2, this.gOff.gain, p.offGain, now, 0.03);
    this.glide(3, this.gSub.gain, p.subGain, now, 0.05);
    this.glide(4, this.lp.frequency, p.cutoff, now, 0.04);
    this.glide(5, this.body.frequency, p.bodyHz, now, 0.1);
    this.glide(6, this.engineGain.gain, p.gain, now, 0.035);
    this.glide(7, this.intakeBp.frequency, p.intakeHz, now, 0.08);
    this.glide(8, this.intakeGain.gain, p.intakeGain * lite, now, 0.05);
    this.glide(9, this.lopeLfo.frequency, p.lopeHz, now, 0.1);
    this.glide(10, this.lopeGain.gain, p.lopeDepth, now, 0.1);

    this.glide(11, this.squealGain.gain, ty.squeal, now, 0.05);
    this.glide(12, this.squealBp.frequency, ty.squealHz, now, 0.08);
    this.glide(13, this.squealBp2.frequency, ty.squealHz * 2.13, now, 0.08);
    this.glide(14, this.scrubGain.gain, ty.scrub, now, 0.06);
    this.glide(15, this.scrapeGain.gain, ty.scrape, now, 0.05);
    this.glide(16, this.scrapeBp.frequency, ty.scrapeHz, now, 0.08);

    this.glide(17, this.air.frequency, sp.cutoff, now, 0.1);
    this.glide(18, this.panner.pan, sp.pan, now, 0.08);
    this.glide(19, this.out.gain, sp.gain, now, 0.06);
  }

  /** The target last scheduled per param slot of apply() (NaN: none, or overridden since). */
  private readonly targets = new Float64Array(20).fill(NaN);

  /**
   * `setTargetAtTime` unless the param is already gliding to (within 0.1 %, or 1e-4 absolute) this target:
   * the approach then carries on unchanged, and an identical call every frame only cost a main-thread →
   * audio-thread message. Idle params (squeal, scrub, lope, the body) now cost nothing; 20 calls per
   * voice per frame came to 3–6 ms/s on the dev box (docs/PERF.md "Phone pass").
   */
  private glide(slot: number, param: AudioParam, v: number, now: number, tc: number): void {
    const last = this.targets[slot]!;
    if (Math.abs(v - last) <= Math.max(1e-4, Math.abs(v) * 1e-3)) return;
    this.targets[slot] = v;
    param.setTargetAtTime(v, now, tc);
  }

  /** Release: fade the voice out. */
  fadeOut(now: number): void {
    if (this.disposed) return;
    this.out.gain.setTargetAtTime(0, now, 0.08);
    this.targets[19] = NaN;
  }

  /**
   * An overrun pop/crackle at audio time `t`: a short dark noise crack plus a little thump, through the
   * voice's own air/pan chain.
   */
  pop(t: number, strength: number): void {
    if (this.disposed) return;
    const ctx = this.ctx;
    const s = Math.max(0, Math.min(1, strength));
    const src = ctx.createBufferSource();
    src.buffer = this.host.noise.brown;
    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.value = 900 + Math.random() * 900;
    lp.Q.value = 0.9;
    const g = ctx.createGain();
    const dur = 0.02 + Math.random() * 0.035;
    g.gain.setValueAtTime(0.0001, t);
    g.gain.linearRampToValueAtTime(POP_GAIN * s, t + 0.002);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    src.connect(lp).connect(g).connect(this.post);
    src.onended = () => {
      src.disconnect();
      lp.disconnect();
      g.disconnect();
    };
    src.start(t, Math.random() * 2);
    src.stop(t + dur + 0.02);
  }

  /** Stop and disconnect everything. */
  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    for (const s of this.sources) {
      try {
        s.stop();
      } catch {
        /* not started */
      }
    }
    for (const n of this.all) {
      try {
        n.disconnect();
      } catch {
        /* gone */
      }
    }
  }
}
